/**
 * Prescription Templates — read queries (PRD.218).
 *
 * Org-level reusable prescription presets. Templates contain one or more
 * medicine rows that may be inventory-linked or free-text. Edits to a
 * template do NOT affect existing `patient_prescriptions` rows.
 *
 * The builder UI writes directly via PowerSync's local SQLite layer; the
 * syncWrite handler propagates inserts/updates to Postgres. This module is
 * read-only.
 */

import type { QuerySpec } from '@helios/powersync-client-core';

// ─── Row types ───

export interface PrescriptionTemplateRow {
  id: string;
  org_id: string;
  name: string;
  description: string | null;
  category: string | null;
  specialty: string | null;
  diagnosis_code: string | null;
  diagnosis_label: string | null;
  default_instructions: string | null;
  is_active: number; // SQLite stores boolean as 0/1
  created_by: string | null;
  created_at: string | null;
  updated_at: string | null;
}

/**
 * Field shape mirrors `patient_prescriptions` so PRD.219 can copy values 1:1
 * when applying a template into an encounter prescription.
 */
export interface PrescriptionTemplateItemRow {
  id: string;
  org_id: string;
  template_id: string;
  inventory_item_id: string | null;
  // Item identity
  item_name: string;
  item_details: string | null;
  // Snapshots from inventory at authoring time
  item_name_snapshot: string | null;
  sku_snapshot: string | null;
  generic_name_snapshot: string | null;
  brand_name_snapshot: string | null;
  strength_snapshot: string | null;
  dosage_form_snapshot: string | null;
  route_snapshot: string | null;
  unit_snapshot: string | null;
  // Prescribing fields (same as patient_prescriptions)
  intake_timing: string | null;
  dose_quantity: string | null;
  schedule_morning: number;
  schedule_afternoon: number;
  schedule_night: number;
  duration_value: number | null;
  duration_unit: string | null;
  instructions: string | null;
  sort_order: number;
  created_at: string | null;
  updated_at: string | null;
}

/**
 * List row including a derived medicine count and a comma-joined snapshot
 * preview for fuzzy text search against medicine names.
 */
export interface PrescriptionTemplateListRow extends PrescriptionTemplateRow {
  medicine_count: number;
  medicine_names_concat: string | null;
}

// ─── Filters ───

export type PrescriptionTemplateStatusFilter = 'active' | 'inactive' | 'all';

export interface PrescriptionTemplatesListOptions {
  orgId: string;
  status?: PrescriptionTemplateStatusFilter;
  /** Free-text search across name, description, category, diagnosis, and medicine snapshot names. */
  search?: string;
  /** Optional category exact-match filter. */
  category?: string | null;
}

// ─── Queries ───

/**
 * List prescription templates for an org with medicine counts and a search
 * helper string built from the item snapshots. Newest-updated first.
 */
export function qPrescriptionTemplates(
  options: PrescriptionTemplatesListOptions,
): QuerySpec<unknown[]> {
  const { orgId, status = 'active', search = '', category = null } = options;
  const term = `%${search.trim().toLowerCase()}%`;
  const hasSearch = search.trim().length > 0;
  const hasCategory = category != null && category.length > 0;

  const statusClause =
    status === 'active'
      ? 'AND COALESCE(t.is_active, 1) = 1'
      : status === 'inactive'
        ? 'AND COALESCE(t.is_active, 1) = 0'
        : '';

  const searchClause = hasSearch
    ? `AND (
        LOWER(COALESCE(t.name, ''))            LIKE ? OR
        LOWER(COALESCE(t.description, ''))     LIKE ? OR
        LOWER(COALESCE(t.category, ''))        LIKE ? OR
        LOWER(COALESCE(t.diagnosis_code, ''))  LIKE ? OR
        LOWER(COALESCE(t.diagnosis_label, '')) LIKE ? OR
        LOWER(COALESCE(items.names, ''))       LIKE ?
      )`
    : '';
  const categoryClause = hasCategory ? `AND t.category = ?` : '';

  const params: unknown[] = [orgId];
  if (hasSearch) {
    params.push(term, term, term, term, term, term);
  }
  if (hasCategory) {
    params.push(category);
  }

  return {
    key: ['prescription_templates', 'list', orgId, status, search, category ?? ''] as const,
    sql: `
      SELECT
        t.*,
        COALESCE(items.cnt, 0)  AS medicine_count,
        items.names             AS medicine_names_concat
      FROM prescription_templates t
      LEFT JOIN (
        SELECT
          template_id,
          COUNT(*) AS cnt,
          GROUP_CONCAT(LOWER(COALESCE(item_name, '')), '|') AS names
        FROM prescription_template_items
        GROUP BY template_id
      ) items ON items.template_id = t.id
      WHERE t.org_id = ?
        ${statusClause}
        ${searchClause}
        ${categoryClause}
      ORDER BY t.updated_at DESC, t.created_at DESC
    `,
    params,
    watch: ['prescription_templates', 'prescription_template_items'],
  };
}

/** Fetch a single template by id. */
export function qPrescriptionTemplateById(templateId: string): QuerySpec<[string]> {
  return {
    key: ['prescription_templates', 'by-id', templateId] as const,
    sql: `SELECT * FROM prescription_templates WHERE id = ? LIMIT 1`,
    params: [templateId],
    watch: ['prescription_templates'],
  };
}

/** Fetch all medicine rows for a template, ordered by sort_order then created_at. */
export function qPrescriptionTemplateItems(templateId: string): QuerySpec<[string]> {
  return {
    key: ['prescription_template_items', 'by-template', templateId] as const,
    sql: `
      SELECT *
      FROM prescription_template_items
      WHERE template_id = ?
      ORDER BY sort_order ASC, created_at ASC
    `,
    params: [templateId],
    watch: ['prescription_template_items'],
  };
}

/** Distinct categories used by templates in an org — useful for filters. */
export function qPrescriptionTemplateCategories(orgId: string): QuerySpec<[string]> {
  return {
    key: ['prescription_templates', 'categories', orgId] as const,
    sql: `
      SELECT DISTINCT category
      FROM prescription_templates
      WHERE org_id = ? AND category IS NOT NULL AND category != ''
      ORDER BY category ASC
    `,
    params: [orgId],
    watch: ['prescription_templates'],
  };
}
